import { useState } from 'react';

import { ZERO } from '@/constants';
import { useView } from '@/hooks';
import { AppMessageSeverities, type User } from '@/types';
import { Paper, Typography } from '@/ui';

import AddCoachModal from '../Add';
import RemoveCoachModal from '../Remove';

import Row from './Row';

import type { FC } from 'react';

import { useRemoveCoachAssignmentMutation } from '@/store/api/coachAssignmentApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';

interface CoachesListProps {
  client: User;
  closeModal: () => void;
  modalOpen: boolean;
}

const CoachesList: FC<CoachesListProps> = ({
  client,
  closeModal,
  modalOpen,
}) => {
  const { coaches } = client;
  const { refetchSelectedUser, selectedUser } = useView();
  const dispatch = useAppDispatch();
  const [processing, setProcessing] = useState(false);
  const [removing, setRemoving] = useState('');

  const [removeCoachAssignment] = useRemoveCoachAssignmentMutation();
  const onRemove = async () => {
    try {
      setProcessing(true);
      await removeCoachAssignment(removing).unwrap();
      await refetchSelectedUser();
      setProcessing(false);
      setRemoving('');
      dispatch(
        setAppMessage({
          message: 'Association removed',
          open: true,
          severity: AppMessageSeverities.Success,
        }),
      );
    } catch (e) {
      setProcessing(false);
      console.error(e);
    }
  };

  const handleRemoveClick = (coachId: string) => {
    if (!selectedUser.coachAssignments) {
      dispatch(
        setAppMessage({
          message: 'Something went wrong',
          open: true,
          severity: AppMessageSeverities.Error,
        }),
      );
      return;
    }
    const assignment = selectedUser.coachAssignments.find(
      (x) => x.coachId === coachId,
    );
    if (!assignment) {
      dispatch(
        setAppMessage({
          message: 'Something went wrong',
          open: true,
          severity: AppMessageSeverities.Error,
        }),
      );
      return;
    }
    setRemoving(assignment.id);
  };

  return (
    <>
      <Paper className="mt-4" variant="outlined">
        {coaches.length === ZERO ? (
          <Typography className="m-6">No Coaches Found</Typography>
        ) : (
          coaches.map((coach) => (
            <Row
              key={coach.id}
              coach={coach}
              processing={false}
              removeClick={() => {
                handleRemoveClick(coach.id);
              }}
            />
          ))
        )}
      </Paper>
      <AddCoachModal
        client={client}
        close={closeModal}
        excludedCoachIds={coaches.map(({ id }) => id)}
        isOpen={modalOpen}
      />
      <RemoveCoachModal
        close={() => {
          setRemoving('');
        }}
        isOpen={!!removing}
        processing={processing}
        onRemove={onRemove}
      />
    </>
  );
};

export default CoachesList;
